import { useState } from "react";

// LinkedIn-style light theme with TrackFin brand teal as primary accent
export const C = {
  bg:        "#f4f2ee",          // page background (LinkedIn cream)
  surface:   "#f3f2f0",          // input backgrounds
  card:      "#ffffff",          // card surface
  border:    "rgba(0, 0, 0, 0.08)",
  borderStrong: "rgba(0, 0, 0, 0.15)",

  text:      "rgba(0, 0, 0, 0.9)",
  muted:     "rgba(0, 0, 0, 0.6)",
  faint:     "rgba(0, 0, 0, 0.45)",

  // LinkedIn-style grey scale for secondary actions
  greyLight: "rgba(0, 0, 0, 0.06)",   // unselected chip / button background
  greyHover: "rgba(0, 0, 0, 0.1)",    // hover on light grey
  greyDark:  "rgba(0, 0, 0, 0.85)",   // selected chip / active toggle background
  greyOnDark:"#ffffff",                // text on dark grey

  // Brand — TrackFin blue + black
  accent:    "#1565d8",          // TrackFin blue — primary CTA
  accentDim: "#1565d815",
  accentDeep:"#0d4ba0",          // hover / pressed state
  black:     "#111111",           // brand black for headings / wordmark

  // LinkedIn-style secondary blue link
  blue:      "#0a66c2",
  blueDim:   "#0a66c215",

  // Status
  success:   "#057642",
  red:       "#cc1016",
  redDim:    "#cc101615",
  gold:      "#915907",
  goldDim:   "#91590715",
  purple:    "#7a3edb",
  orange:    "#e16f3d",
};

export const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export const pct = (v, t) => (t ? ((v / t) * 100).toFixed(1) : 0);

// Categories that are NEEDS — must be covered by emergency fund
export const ESSENTIAL_CATEGORIES = new Set([
  "Rent",        // monthly rent
  "Housing",     // home maintenance, property tax, repairs
  "Food",        // groceries (assume mostly essential)
  "Transport",   // commute to work
  "Healthcare",  // medical bills, premiums
  "Education",   // kids' school fees
  "Utilities",   // electricity, water, gas, internet
  "EMI/Loan",    // legal loan obligations
  "Insurance",   // life, health, term premiums
]);

// Categories that are WANTS — discretionary, not needed for survival
export const DISCRETIONARY_CATEGORIES = new Set([
  "Entertainment",
  "Clothing",
  "Personal Care",
  "Donation",
  "Other",
]);

export const Card = ({ children, style = {}, onClick }) => (
  <div onClick={onClick} style={{
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: "0 0 0 1px rgba(0,0,0,.04)",
    padding: 20,
    ...style,
  }}>
    {children}
  </div>
);

export const Tag = ({ c, children }) => (
  <span style={{
    background: c + "15",
    color: c,
    border: `1px solid ${c}33`,
    borderRadius: 12,
    padding: "2px 10px",
    fontSize: 11,
    fontWeight: 600,
  }}>
    {children}
  </span>
);

export const ProgressBar = ({ value, max, color, height = 6 }) => (
  <div style={{ height, background: "rgba(0,0,0,0.06)", borderRadius: height, overflow: "hidden" }}>
    <div style={{ height: "100%", width: `${Math.min(100, (value / max) * 100)}%`, background: color, borderRadius: height, transition: "width 0.6s ease" }} />
  </div>
);

// Reusable tag/label input. Enter or comma adds a tag; backspace on empty removes last.
export const TagInput = ({ tags = [], onChange, placeholder = "Add tag and press Enter" }) => {
  const [draft, setDraft] = useState("");
  const commit = (val) => {
    const t = String(val || "").trim();
    if (!t) return;
    if (tags.some(x => x.toLowerCase() === t.toLowerCase())) { setDraft(""); return; }
    if (tags.length >= 10) { setDraft(""); return; }
    onChange([...tags, t.slice(0, 30)]);
    setDraft("");
  };
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 8px", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", minHeight: 36 }}>
      {tags.map((t, i) => (
        <span key={i} style={{ background: C.accent + "22", color: C.accent, border: `1px solid ${C.accent}44`, borderRadius: 12, padding: "2px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
          #{t}
          <button type="button" onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
            style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
        </span>
      ))}
      <input value={draft} onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(draft); }
          else if (e.key === "Backspace" && !draft && tags.length > 0) { onChange(tags.slice(0, -1)); }
        }}
        onBlur={() => commit(draft)}
        placeholder={tags.length === 0 ? placeholder : ""}
        style={{ flex: 1, minWidth: 100, background: "transparent", border: "none", outline: "none", color: C.text, fontSize: 13, padding: "4px 0" }} />
    </div>
  );
};
